/**
 * Simple analytics tracking for notification opt-in rates
 */

interface NotificationAnalyticsData {
  promptsShown: number;
  promptsInteracted: number;
  permissionsRequested: number;
  permissionsGranted: number;
  permissionsDenied: number;
}

const ANALYTICS_STORAGE_KEY = 'veripay_notification_analytics';

/**
 * Get analytics data from localStorage
 */
export const getNotificationAnalytics = (): NotificationAnalyticsData => {
  const stored = localStorage.getItem(ANALYTICS_STORAGE_KEY);
  
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse stored analytics', e);
    }
  }
  
  return {
    promptsShown: 0,
    promptsInteracted: 0,
    permissionsRequested: 0,
    permissionsGranted: 0,
    permissionsDenied: 0
  };
};

/**
 * Update analytics data in localStorage
 */
export const updateNotificationAnalytics = (
  updates: Partial<NotificationAnalyticsData>
): NotificationAnalyticsData => {
  const current = getNotificationAnalytics();
  const updated = { ...current, ...updates };
  
  localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

/**
 * Track when a notification prompt is shown
 */
export const trackPromptShown = (): void => {
  const analytics = getNotificationAnalytics();
  updateNotificationAnalytics({ 
    promptsShown: analytics.promptsShown + 1 
  });
};

/**
 * Track when a user interacts with a notification prompt
 */
export const trackPromptInteraction = (): void => {
  const analytics = getNotificationAnalytics();
  updateNotificationAnalytics({ 
    promptsInteracted: analytics.promptsInteracted + 1 
  });
};

/**
 * Track when permission is requested
 */
export const trackPermissionRequested = (): void => {
  const analytics = getNotificationAnalytics();
  updateNotificationAnalytics({ 
    permissionsRequested: analytics.permissionsRequested + 1 
  });
};

/**
 * Track the result of a permission request
 */
export const trackPermissionResult = (result: NotificationPermission): void => {
  const analytics = getNotificationAnalytics();
  
  if (result === 'granted') {
    updateNotificationAnalytics({ 
      permissionsGranted: analytics.permissionsGranted + 1 
    });
  } else if (result === 'denied') {
    updateNotificationAnalytics({ 
      permissionsDenied: analytics.permissionsDenied + 1 
    });
  }
};

/**
 * Calculate the opt-in rate
 */
export const calculateOptInRate = (): number => {
  const analytics = getNotificationAnalytics();
  
  if (analytics.permissionsRequested === 0) {
    return 0;
  }
  
  return analytics.permissionsGranted / analytics.permissionsRequested;
};

/**
 * Get a summary of notification analytics
 */
export const getAnalyticsSummary = (): string => {
  const analytics = getNotificationAnalytics();
  const optInRate = calculateOptInRate();
  
  return `
    Notification Analytics:
    - Prompts shown: ${analytics.promptsShown}
    - Prompts interacted with: ${analytics.promptsInteracted}
    - Permissions requested: ${analytics.permissionsRequested}
    - Permissions granted: ${analytics.permissionsGranted}
    - Permissions denied: ${analytics.permissionsDenied}
    - Opt-in rate: ${(optInRate * 100).toFixed(1)}%
  `;
};